import { createLimiter } from './limiter';

export type EngineEvent = 'ready' | 'play' | 'pause' | 'timeupdate' | 'seek';

export interface StemGainState {
  volume: number; // 0..1, user slider value
  muted: boolean;
  soloed: boolean;
}

const RAMP_S = 0.005;

/**
 * Multi-stem synchronized playback engine.
 *
 * Owns a single AudioContext, four AudioBuffers, per-stem GainNodes, and a
 * master DynamicsCompressor acting as a soft limiter. Playback is loop-seamless:
 * all stems share loopStart/loopEnd and are started with a single scheduling
 * call so they remain sample-accurate regardless of buffer slight-length
 * differences (we clamp all loops to the shortest stem's duration).
 */
export class AudioEngine {
  private ctx: AudioContext;
  private buffers: AudioBuffer[] = [];
  private sources: AudioBufferSourceNode[] = [];
  private gains: GainNode[] = [];
  private master: DynamicsCompressorNode;

  private isPlaying_ = false;
  private startCtxTime = 0; // context time when current play session started
  private pauseOffset = 0; // seconds into the buffer (pre-modulo)
  private duration_ = 0;

  private listeners = new Map<EngineEvent, Set<() => void>>();
  private rafHandle: number | null = null;

  private state: StemGainState[] = [];

  constructor() {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = createLimiter(this.ctx);
    this.master.connect(this.ctx.destination);
  }

  get context(): AudioContext {
    return this.ctx;
  }

  get duration(): number {
    return this.duration_;
  }

  get isPlaying(): boolean {
    return this.isPlaying_;
  }

  get currentTime(): number {
    if (!this.isPlaying_) {
      return this.pauseOffset % (this.duration_ || 1);
    }
    const elapsed = this.ctx.currentTime - this.startCtxTime + this.pauseOffset;
    return this.duration_ > 0 ? elapsed % this.duration_ : 0;
  }

  on(event: EngineEvent, cb: () => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(event: EngineEvent): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of set) cb();
  }

  async setBuffers(buffers: AudioBuffer[]): Promise<void> {
    this.stopInternal();
    this.buffers = buffers;
    // Clamp duration to the shortest stem so loop wrap is consistent.
    this.duration_ = Math.min(...buffers.map((b) => b.duration));

    // Build gain nodes (one per stem).
    for (const g of this.gains) g.disconnect();
    this.gains = buffers.map(() => {
      const g = this.ctx.createGain();
      g.gain.value = 1;
      g.connect(this.master);
      return g;
    });

    // Default all stems unmuted, unsoloed, volume 1.
    this.state = buffers.map(() => ({ volume: 1, muted: false, soloed: false }));
    this.applyStateToGains();
    this.pauseOffset = 0;
    this.emit('ready');
    this.emit('timeupdate');
  }

  setStemVolume(index: number, volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    this.state[index].volume = clamped;
    this.applyStateToGains();
  }

  setStemMuted(index: number, muted: boolean): void {
    this.state[index].muted = muted;
    if (muted) {
      // Muted stem cannot be soloed (matches SoundApp).
      this.state[index].soloed = false;
    }
    this.applyStateToGains();
  }

  setStemSoloed(index: number, soloed: boolean): void {
    if (soloed) {
      // Clicking solo on a stem clears solo on all others and mutes them
      // via the derived-gain rule (effective soloing wins).
      for (let i = 0; i < this.state.length; i++) {
        this.state[i].soloed = i === index;
        if (i === index) this.state[i].muted = false;
      }
    } else {
      this.state[index].soloed = false;
    }
    this.applyStateToGains();
  }

  getStemState(index: number): Readonly<StemGainState> {
    return this.state[index];
  }

  private anySoloed(): boolean {
    return this.state.some((s) => s.soloed);
  }

  private effectiveGain(i: number): number {
    const s = this.state[i];
    if (this.anySoloed()) {
      return s.soloed ? s.volume : 0;
    }
    return s.muted ? 0 : s.volume;
  }

  private applyStateToGains(): void {
    const now = this.ctx.currentTime;
    for (let i = 0; i < this.gains.length; i++) {
      const g = this.effectiveGain(i);
      this.gains[i].gain.setTargetAtTime(g, now, RAMP_S);
    }
  }

  async resume(): Promise<void> {
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
  }

  async play(): Promise<void> {
    if (this.isPlaying_ || this.buffers.length === 0) return;
    await this.resume();
    this.scheduleSources(this.pauseOffset % this.duration_);
    this.isPlaying_ = true;
    this.emit('play');
    this.startRaf();
  }

  pause(): void {
    if (!this.isPlaying_) return;
    const t = this.currentTime;
    this.stopInternal();
    this.pauseOffset = t;
    this.isPlaying_ = false;
    this.stopRaf();
    this.emit('pause');
    this.emit('timeupdate');
  }

  seek(time: number): void {
    const wasPlaying = this.isPlaying_;
    this.stopInternal();
    this.pauseOffset = Math.max(0, Math.min(this.duration_, time));
    this.isPlaying_ = false;
    if (wasPlaying) {
      this.scheduleSources(this.pauseOffset);
      this.isPlaying_ = true;
      this.emit('play');
      this.startRaf();
    }
    this.emit('seek');
    this.emit('timeupdate');
  }

  private scheduleSources(offset: number): void {
    for (const s of this.sources) {
      try { s.stop(); } catch { /* already stopped */ }
      s.disconnect();
    }
    this.sources = this.buffers.map((buffer, i) => {
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.loopStart = 0;
      src.loopEnd = this.duration_;
      src.connect(this.gains[i]);
      return src;
    });
    this.startCtxTime = this.ctx.currentTime;
    const startAt = this.ctx.currentTime; // immediate
    for (const src of this.sources) {
      src.start(startAt, offset);
    }
  }

  private stopInternal(): void {
    for (const s of this.sources) {
      try { s.stop(); } catch { /* already stopped */ }
      s.disconnect();
    }
    this.sources = [];
  }

  private startRaf(): void {
    if (this.rafHandle !== null) return;
    const tick = () => {
      this.emit('timeupdate');
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private stopRaf(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  async dispose(): Promise<void> {
    this.stopRaf();
    this.stopInternal();
    this.listeners.clear();
    try {
      await this.ctx.close();
    } catch { /* ignore */ }
  }
}
